import { Flex, FlexProps } from '@chakra-ui/react'
import { PDFDocumentProxy } from 'pdfjs-dist/types/src/display/api'
import { Suspense, lazy, useEffect, useState } from 'react'
import { DocumentProps, PageProps } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { useElementSize } from 'utils'
import IconButton from '../IconButton'
import Text from '../Text'
import { ChevronLeftIcon, ChevronRightIcon } from '../icons'

export interface Props extends FlexProps {
  documentProps?: DocumentProps
  file: string | File | ArrayBuffer // URL, File object, or ArrayBuffer
  onLoadError?: (error: Error) => void
  onLoadSuccess?: (pdf: { numPages: number }) => void
  pageNumber?: number
  pageProps?: PageProps
  scrollBarWidth?: number
}

const Document = lazy(() => import('react-pdf').then(m => ({ default: m.Document })))
const Page = lazy(() => import('react-pdf').then(m => ({ default: m.Page })))

export default function PDFViewer({
  file,
  pageProps,
  pageNumber: parentPageNumber = 1,
  documentProps,
  scrollBarWidth = 0,
  ...rest
}: Props) {
  const { ref, width } = useElementSize()
  const [numPages, setNumPages] = useState<number>(0)
  const [pageNumber, setPageNumber] = useState<number>(parentPageNumber)

  init()

  useEffect(() => {
    setPageNumber(parentPageNumber)
  }, [parentPageNumber])

  function handleLoadSuccess(res: PDFDocumentProxy) {
    setNumPages(res.numPages)
    documentProps?.onLoadSuccess?.(res)
  }

  function handleLoadError(error: Error) {
    documentProps?.onLoadError?.(error)
  }

  function goToPrevPage() {
    setPageNumber(prevPageNumber => Math.max(prevPageNumber - 1, 1))
  }

  function goToNextPage() {
    setPageNumber(prevPageNumber => Math.min(prevPageNumber + 1, numPages))
  }

  return (
    <Flex ref={ref} direction="column" overflow="hidden" width="100%" {...rest}>
      <Flex overflow="auto" flex={1}>
        <Suspense>
          <Document
            file={file}
            onLoadSuccess={handleLoadSuccess}
            onLoadError={handleLoadError}
            {...documentProps}
          >
            <Page
              pageNumber={pageNumber}
              renderTextLayer
              renderAnnotationLayer
              width={width - scrollBarWidth}
              {...pageProps}
            />
          </Document>
        </Suspense>
      </Flex>

      {numPages > 1 && (
        <Flex justifyContent="flex-end" alignItems="center" gap={2}>
          <Text>
            Page {pageNumber} of {numPages}
          </Text>
          <IconButton
            onClick={goToPrevPage}
            aria-label="Previous Page"
            icon={<ChevronLeftIcon />}
            disabled={pageNumber <= 1}
            style={{ cursor: pageNumber <= 1 ? 'not-allowed' : 'pointer' }}
          />
          <IconButton
            onClick={goToNextPage}
            aria-label="Next Page"
            icon={<ChevronRightIcon />}
            disabled={pageNumber >= numPages}
            style={{ cursor: pageNumber >= numPages ? 'not-allowed' : 'pointer' }}
          />
        </Flex>
      )}
    </Flex>
  )
}

let initialized = false
async function init() {
  if (initialized) return
  initialized = true

  const { pdfjs } = await import('react-pdf')
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url
  ).toString()
}
